import {
  Box,
  BoxContent,
  BoxSidebar,
  BoxSidebarItem,
  Heading,
  TextLink,
} from '@mm/components';
import { InfoIcon } from '@mm/icons';
import { selectOverviewCountByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { PaginationNav } from '../../../../../common/components/PaginationNav/PaginationNav';
import { SearchInput } from '../../../../../common/components/SearchInput/SearchInput';
import { useActivePageMessagingStatus } from '../../../campaigns/components/BlastTypeSelector/BlastTypeSelector';
import { November2020EuroAdsChangesAlert } from '../../../clickToMessengerAds/components/November2020EuroAdsChangesAlert/November2020EuroAdsChangesAlert';
import './Campaigns.pcss';
import { CampaignsEmptyState } from './CampaignsEmptyState/CampaignsEmptyState';
import { CampaignsFilterByKind } from './CampaignsFilterByKind/CampaignsFilterByKind';
import { CampaignsTable } from './CampaignsTable/CampaignsTable';
import { ConnectPageBanner } from './ConnectPageBanner/ConnectPageBanner';
import { CreateCampaignButton } from './CreateCampaignButton/CreateCampaignButton';
import { useFetchCampaigns } from './use-fetch-campaigns';

type Props = {
  className?: string;
};

export const Campaigns = ({ className }: Props) => {
  const { isMessagingApproved } = useActivePageMessagingStatus();

  const {
    setFilterKind,
    activeKind,
    records,
    changePage,
    activeSearch,
    setSearchQuery,
    fetchCurrentActiveKind,
  } = useFetchCampaigns();

  const count = useSelector((state: StoreState) =>
    selectOverviewCountByUiFilterKind(state, activeKind),
  );

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const campaigns = records?.campaigns;
  const pagination = records?.pagination;

  const isEmpty = !records?.pagination?.total;

  const isCtmOrSponsoredKind =
    activeKind === 'ctm_ads' || activeKind === 'sponsored_messages';

  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  return (
    <section className={classNames(className, 'campaigns')}>
      <Box className="campaigns__box">
        <BoxSidebar className="campaigns__box-sidebar">
          {/* TODO waiting for BE implementation */}
          {/*<BoxSidebarItem>*/}
          {/*  <Heading*/}
          {/*    className="campaigns__filter-heading"*/}
          {/*    text="View by time"*/}
          {/*    size={5}*/}
          {/*  />*/}
          {/*  <CampaignsFilterByTime*/}
          {/*    activeTime={activeTime}*/}
          {/*    onChange={setFilterTime}*/}
          {/*  />*/}
          {/*</BoxSidebarItem>*/}
          <BoxSidebarItem>
            <Heading
              className="campaigns__filter-heading"
              text="View by type"
              size={5}
            />
            <CampaignsFilterByKind
              activeKind={activeKind}
              onChange={setFilterKind}
              messagingApproved={isMessagingApproved}
            />
          </BoxSidebarItem>
        </BoxSidebar>

        <BoxContent className="campaign__content">
          {isCtmOrSponsoredKind && <November2020EuroAdsChangesAlert />}

          {!isUserPageConnected && <ConnectPageBanner kind={activeKind} />}

          <div className="campaigns__content-topbar">
            <CreateCampaignButton
              className="campaigns__create-button"
              kind={activeKind}
              prepend="Create"
              withPlusIcon
            />
          </div>
          <div className="campaigns__content-midbar">
            <SearchInput
              className="campaigns__search-input"
              placeholder="Search"
              value={activeSearch}
              onChange={onSearchInputChange}
            />

            <div className="campaigns__info-message">
              <InfoIcon className="campaigns__info-icon" />
              <TextLink>Statistics may be behind by a few minutes</TextLink>
            </div>
          </div>

          <div className="campaigns__content-data">
            {campaigns && (
              <CampaignsTable
                className="campaigns__content-table"
                campaigns={campaigns}
                fetchCurrentActiveKind={() =>
                  fetchCurrentActiveKind(pagination)
                }
              />
            )}
            {isEmpty && <CampaignsEmptyState kind={activeKind} />}
          </div>

          <div className="campaigns__content-footer">
            {pagination && (
              <PaginationNav
                pagination={pagination}
                onChangePage={changePage}
                rangeLength={5}
                circularActive
                withEllipsis
              />
            )}
            {!!count && (
              <TextLink className="campaigns__total">
                Found &nbsp;
                <span className="campaigns__total-count">{count}</span>&nbsp;
                items
              </TextLink>
            )}
          </div>
        </BoxContent>
      </Box>
    </section>
  );
};
